import { useState } from 'react';
import { Button, Pill, Screen, ScreenHeader, SectionHead, type PillTone } from '@garnet/ui';
import type { Connector, ProviderLink, VerticalData } from '../data/types.js';
import { ConnectModal } from '../components/ConnectModal.js';

/* Garnet · Settings · integrations — source health + three connect surfaces
   (Sourcing & RFP · CRM · GRC), all sharing the redirect-first connect flow (§7.4). */

type Category = 'sourcing' | 'crm' | 'grc';
type CatProvider = ProviderLink & { category: Category; justConnected?: boolean; account?: string | null };

const CSTAT: Record<Connector['status'], PillTone> = { live: 'm', degraded: 'a', down: 'f', planned: 'n' };
const CONN: Record<ProviderLink['connection'], PillTone> = { connected: 'm', disconnected: 'n', error: 'f' };
const SCOL = '1.6fr 1.25fr 1.5fr 132px 96px';

/* one dense integration panel — provider · instance · flows · connection · action */
function IntegrationRows({
  title,
  sub,
  rows,
  onOpen,
  footnote,
}: {
  title: string;
  sub: string;
  rows: CatProvider[];
  onOpen: (s: CatProvider) => void;
  footnote?: string;
}) {
  return (
    <div>
      <SectionHead title={title} sub={sub} />
      <div className="panel">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: SCOL,
            gap: 12,
            padding: '10px 16px',
            borderBottom: '1px solid var(--ink)',
            fontFamily: 'var(--mono)',
            fontSize: 10,
            letterSpacing: '.06em',
            textTransform: 'uppercase',
            color: 'var(--muted)',
          }}
        >
          <span>system</span>
          <span>instance</span>
          <span>data flows</span>
          <span>connection</span>
          <span style={{ textAlign: 'right' }}>action</span>
        </div>
        {rows.map((s, i) => (
          <div
            key={s.name}
            style={{
              display: 'grid',
              gridTemplateColumns: SCOL,
              gap: 12,
              padding: '13px 16px',
              borderBottom: i < rows.length - 1 ? '1px solid var(--rule)' : 0,
              alignItems: 'center',
              background: s.generic ? 'var(--rule-soft)' : 'transparent',
            }}
          >
            {/* system */}
            <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 11.5,
                  fontWeight: 700,
                  width: 30,
                  height: 30,
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid var(--rule)',
                  borderRadius: 5,
                  borderStyle: s.generic ? 'dashed' : 'solid',
                  color: s.generic ? 'var(--muted)' : 'var(--ink)',
                }}
              >
                {s.glyph}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{s.name}</div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>{s.auth}</div>
                {s.note && (
                  <div
                    style={{
                      fontSize: 11,
                      lineHeight: 1.45,
                      marginTop: 5,
                      color: s.connection === 'error' ? 'var(--flag-ink)' : 'var(--muted)',
                    }}
                  >
                    {s.note}
                  </div>
                )}
              </div>
            </div>
            {/* instance */}
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 9.5,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                }}
              >
                {s.instLabel}
              </div>
              <div
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 12,
                  color: s.instance === '—' ? 'var(--muted)' : 'var(--ink)',
                  marginTop: 3,
                  wordBreak: 'break-all',
                }}
              >
                {s.instance}
              </div>
            </div>
            {/* flows */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {s.flows.map((f) => (
                <span
                  key={f}
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    color: 'var(--muted)',
                    border: '1px solid var(--rule)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '2px 6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {f}
                </span>
              ))}
            </div>
            {/* connection */}
            <div>
              <Pill tone={CONN[s.connection]}>{s.connection}</Pill>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)', marginTop: 6 }}>
                {s.connection === 'connected'
                  ? (s.records ? s.records.toLocaleString() + ' recs · ' : '') + s.last
                  : s.connection === 'error'
                    ? 'last ok · ' + s.last
                    : 'never synced'}
              </div>
            </div>
            {/* action */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {s.connection === 'connected' ? (
                <Button sm variant="secondary" onClick={() => onOpen(s)}>
                  Manage
                </Button>
              ) : (
                <Button sm variant="primary" icon="link" onClick={() => onOpen(s)}>
                  {s.connection === 'error' ? 'Reconnect' : 'Connect'}
                </Button>
              )}
            </div>
          </div>
        ))}
        {footnote && (
          <div
            style={{
              padding: '9px 16px',
              borderTop: '1px solid var(--rule)',
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              color: 'var(--muted)',
              lineHeight: 1.5,
            }}
          >
            {footnote}
          </div>
        )}
      </div>
    </div>
  );
}

function withCat(list: ProviderLink[], cat: Category): CatProvider[] {
  return list.map((p) => ({ ...p, category: cat }));
}

export function Settings({ data }: { data: VerticalData }) {
  const [sourcing, setSourcing] = useState<CatProvider[]>(() => withCat(data.sourcing, 'sourcing'));
  const [crms, setCrms] = useState<CatProvider[]>(() => withCat(data.crms, 'crm'));
  const [grcs, setGrcs] = useState<CatProvider[]>(() => withCat(data.grcs, 'grc'));
  const [connectFor, setConnectFor] = useState<{ provider: CatProvider; entry: string } | null>(null);

  function resolveProvider(name: string, patch: Partial<ProviderLink> & Record<string, unknown>) {
    const upd = (list: CatProvider[]): CatProvider[] =>
      list.map((p) => (p.name === name ? ({ ...p, ...patch } as CatProvider) : p));
    setSourcing(upd);
    setCrms(upd);
    setGrcs(upd);
    setConnectFor((cf) =>
      cf && cf.provider.name === name ? { ...cf, provider: { ...cf.provider, ...patch } as CatProvider } : cf,
    );
  }
  function openConnect(s: CatProvider) {
    setConnectFor({
      provider: s,
      entry: s.connection === 'connected' ? 'connected' : s.connection === 'error' ? 'failure' : 'creds',
    });
  }

  const connectors = data.connectors;

  return (
    <Screen>
      <ScreenHeader eyebrow="§ Settings · integrations" title="Integrations & source health" />

      {/* source health */}
      <div>
        <SectionHead title="Source health" sub="freshness · quota · residency, as this org's runs see it" />
        <div className="panel">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1.4fr 110px 1fr 70px',
              gap: 12,
              padding: '10px 16px',
              borderBottom: '1px solid var(--ink)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
            }}
          >
            <span>source</span>
            <span>scope</span>
            <span>status</span>
            <span>quota</span>
            <span style={{ textAlign: 'right' }}>region</span>
          </div>
          {connectors.map((c, i) => (
            <div
              key={c.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.4fr 110px 1fr 70px',
                gap: 12,
                padding: '11px 16px',
                borderBottom: i < connectors.length - 1 ? '1px solid var(--rule)' : 0,
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                {c.note && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{c.note}</div>}
              </div>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>{c.scope}</span>
              <Pill tone={CSTAT[c.status]}>{c.status}</Pill>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 6, background: 'var(--rule-soft)', borderRadius: 999, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: c.quota + '%',
                      height: '100%',
                      background: c.quota > 50 ? 'var(--verified)' : c.quota > 0 ? 'var(--attest)' : 'var(--rule)',
                    }}
                  />
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)', minWidth: 42 }}>{c.freshness}</span>
              </div>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)', textAlign: 'right' }}>{c.residency}</span>
            </div>
          ))}
        </div>
      </div>

      <IntegrationRows
        title="Sourcing & RFP"
        sub="suppliers and RFx events flow in from procurement systems; diligence runs on intake"
        rows={sourcing}
        onOpen={openConnect}
        footnote="5 named systems · 1 generic connector · new suppliers land in the document inbox as a diligence intake"
      />

      <IntegrationRows
        title="CRM"
        sub="supplier records and qualification state sync two-way with the system of relationship"
        rows={crms}
        onOpen={openConnect}
        footnote="qualification state and risk tier write back to the linked CRM"
      />

      <IntegrationRows
        title="GRC"
        sub="verification records, exceptions and evidence push to the governance / risk system of record"
        rows={grcs}
        onOpen={openConnect}
        footnote="5 named systems · 1 generic connector · exceptions open as issues with evidence attached"
      />

      {connectFor && (
        <ConnectModal
          key={connectFor.provider.name}
          provider={connectFor.provider}
          entry={connectFor.entry}
          onClose={() => setConnectFor(null)}
          onResolve={resolveProvider}
        />
      )}
    </Screen>
  );
}
